/**
 * External dependencies
 */
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  YAxis,
  LabelList,
} from "recharts";
import { size, get } from "lodash";

/**
 * Internal dependencies
 */
import { COLORS, COMPARISON_COLORS } from "./constants";
import {
  toYAxisFormatter,
  formatResultSetToShortTitle,
  formatMaybeDateString,
  shouldColorCells,
  toNormalizedSeriesNames,
  toNormalizedResultData,
  isInFirstHalf,
} from "./utils";
import EmptyContent from "./base/empty-content";
import { CustomTooltip } from "../custom-tooltip";
import { CustomLegend } from "../custom-legend";

export default ({
  resultSet,
  xAxisLabel,
  yAxisLabel,
  onBarClick,
  dataType,
  limit,
  stacked,
  colorTheme,
  rowTotals,
  hideLegend,
  axisTickAngle = 0,
  compare,
  ...r
}) => {
  let formatter = toYAxisFormatter(resultSet);

  let data = toNormalizedResultData(resultSet);

  if (size(data) === 0) {
    return <EmptyContent />;
  }

  const normalizedSeries = toNormalizedSeriesNames(resultSet);

  const colorCells = React.useMemo(() => {
    return shouldColorCells(resultSet);
  }, []);

  if (colorCells) {
    data = data.map((d, i) => ({
      ...d,
      fill: get(COLORS, [colorTheme || "default", i]),
    }));
  }

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      className="chart__column-chart"
    >
      <BarChart
        barGap={0}
        data={data}
        barCategoryGap={dataType === "time_series" ? "10%" : 10}
        margin={{ top: 20, right: 40, bottom: 10, left: 15 }}
      >
        <CartesianGrid stroke="#eee" vertical={false} />
        <Tooltip dataKey="" content={CustomTooltip} formatter={formatter} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={formatter}
          tick={{ fill: "#33475b", fontSize: 10 }}
          domain={[0, 0]}
          label={{
            angle: -90,
            position: "insideLeft",
            style: { fill: "#33475b", fontSize: 11 },
            ...yAxisLabel,
          }}
        />
        {hideLegend ? null : (
          <Legend
            verticalAlign="top"
            align="left"
            iconType="square"
            wrapperStyle={{ paddingBottom: 25 }}
            iconSize={10}
            content={(p) => <CustomLegend {...p} isCompare={compare} />}
          />
        )}
        <XAxis
          allowDataOverflow={false}
          stroke="#7c98b6"
          tick={{ fill: "#33475b", fontSize: 10 }}
          domain={[0, 0]}
          dataKey="x"
          tickLine={false}
          height={30}
          tickMargin={10}
          type="category"
          angle={axisTickAngle}
          tickFormatter={(v) => formatMaybeDateString(v)}
          label={{
            position: "bottom",
            style: { fill: "#82c0fd", fontSize: 11 },
            ...xAxisLabel,
          }}
        />
        {normalizedSeries.map((series, i) => {
          const isFirstHalf = isInFirstHalf(normalizedSeries, i);

          const colors =
            compare === true
              ? isFirstHalf
                ? COLORS
                : COMPARISON_COLORS
              : COLORS;

          return (
            <Bar
              key={series.key}
              stackId={
                stacked
                  ? get(resultSet, "queryType") == "regularQuery"
                    ? 1
                    : get(series, ["yValues", "0"])
                  : null
              }
              dataKey={series.key}
              name={formatResultSetToShortTitle(resultSet.queryType, series)}
              onClick={onBarClick}
              fill={get(colors, [colorTheme || "default", i])}
              fillOpacity={!compare || isFirstHalf ? 1 : 0.4}
              tooltipItemColor={get(colors, [colorTheme || "default", i])}
            >
              {rowTotals && <LabelList dataKey={series.key} position="top" />}
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};
